/**
 * Instance options API — the option lists behind the Add Instance form's
 * Region and Instance Type dropdowns.
 *
 * The legacy calls (apiservice.js:373-381 and :458-465):
 *   GET /regions?provider=<CSP>&email=<user>                 -> { regions: [...] }
 *   GET /instance-sizes?provider=<CSP>&region=<r>&email=<user> -> { instances: [...] }
 *   GET /cloud-instances?provider=<CSP>&email=<user>           -> { instances: [...] }
 *     (every instance type of the provider, any region — Find and Replace's
 *      "To" list, legacy getListofInstance apiservice.js:540-547)
 *
 * Legacy fetches regions whenever the provider changes and instance sizes
 * whenever the region changes (ManageAccount.vue:596-600, 786-810) — including
 * back to one already loaded. Same here: hooks/useInstanceOptions asks for a
 * refetch on every change rather than answering from the cache.
 *
 * `provider` is the sidebar's Service Provider (AWS / AZURE / GCP / OCI). Legacy
 * also maps telemetry connector names to a CSP here ("AWS CloudWatch" -> AWS);
 * not needed — in this app the provider field only ever holds a CSP.
 *
 * Same request for CCA and EIA: the product travels as the Appname header.
 */
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { baseApi } from '@services/baseApi';

const { REGIONS, INSTANCE_SIZES, CLOUD_INSTANCES } = API_ENDPOINTS.MANAGE_PORTFOLIO;

/**
 * The option list out of a response field, always an array. Empty / missing
 * entries are dropped so the dropdown never shows a blank row.
 */
export function readOptions(response, field) {
  const list = response?.[field];
  return Array.isArray(list) ? list.filter((item) => item != null && item !== '') : [];
}

export const instanceOptionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRegions: builder.query({
      query: ({ provider, email }) => ({
        url: REGIONS,
        params: { provider, email },
      }),
      transformResponse: (response) => readOptions(response, 'regions'),
    }),

    getInstanceSizes: builder.query({
      query: ({ provider, region, email }) => ({
        url: INSTANCE_SIZES,
        params: { provider, region, email },
      }),
      transformResponse: (response) => readOptions(response, 'instances'),
    }),

    getCloudInstances: builder.query({
      query: ({ provider, email }) => ({
        url: CLOUD_INSTANCES,
        params: { provider, email },
      }),
      transformResponse: (response) => readOptions(response, 'instances'),
    }),
  }),
});

export const { useGetRegionsQuery, useGetInstanceSizesQuery, useGetCloudInstancesQuery } =
  instanceOptionsApi;
